import { z } from 'zod';
/**
 * Chat integrations — Slack and Microsoft Teams apps a tenant installs so hosts are told about
 * a visitor in chat as well as by email (docs/prd/PRD-010-chat-integrations-slack-teams.md).
 *
 * Tenant-level and admin-only: read with `platform.integrations.read`, changed with
 * `platform.integrations.manage`. Every DTO here is TENANT-scoped and never carries `tenantId`
 * or any vendor credential — the bot token stays encrypted on the server (PRD-010 "Security").
 */
/** Database enum `ChatProvider`, in lock-step (`packages/db/src/chat-integration-enums.test.ts`). */
export const CHAT_PROVIDERS = ['SLACK', 'TEAMS'];
export const chatProviderSchema = z.enum(CHAT_PROVIDERS);
/** Vendor brand names — never translated (docs/011), so they live here rather than in a locale bundle. */
export const CHAT_PROVIDER_NAMES = {
    SLACK: 'Slack',
    TEAMS: 'Microsoft Teams',
};
/** Database enum `ChatIntegrationStatus`, in lock-step with the same test. */
export const CHAT_INTEGRATION_STATUSES = ['ACTIVE', 'REVOKED', 'ERROR'];
export const chatIntegrationStatusSchema = z.enum(CHAT_INTEGRATION_STATUSES);
/**
 * The provider as it appears in a URL (`/tenant/integrations/:provider/install`) — lower case,
 * and the same word the OAuth redirect URI ends in, so the vendor console and the route agree.
 */
export const CHAT_PROVIDER_SLUGS = ['slack', 'teams'];
export const chatProviderSlugSchema = z.enum(CHAT_PROVIDER_SLUGS);
const PROVIDER_BY_SLUG = { slack: 'SLACK', teams: 'TEAMS' };
export function chatProviderFromSlug(slug) {
    return PROVIDER_BY_SLUG[slug];
}
export function chatProviderSlug(provider) {
    return provider === 'SLACK' ? 'slack' : 'teams';
}
/** One installed integration. `locationIds` empty = every location (PRD-010). */
export const chatIntegrationDTOSchema = z.object({
    id: z.string().min(1),
    provider: chatProviderSchema,
    /** The workspace's display name, e.g. "Acme HQ". */
    externalTenantName: z.string(),
    status: chatIntegrationStatusSchema,
    scopes: z.array(z.string()),
    installedAt: z.string().datetime(),
    locationIds: z.array(z.string()),
    /**
     * PRD-010 CHAT-09: whether the install granted what posting to a shared channel needs. `false`
     * for a workspace connected before channel posting existed — the card offers Reconnect.
     */
    channelPostingReady: z.boolean(),
});
/**
 * One card on Settings → Integrations. `available` is false for a provider this deployment
 * has no adapter for (Teams in slice 1, or Slack where `CHAT_SLACK_ADAPTER=disabled`), so the
 * UI renders "Coming soon" rather than a Connect button that cannot work.
 */
export const chatProviderStateDTOSchema = z.object({
    provider: chatProviderSchema,
    available: z.boolean(),
    integration: chatIntegrationDTOSchema.nullable(),
});
/** `GET /api/web/tenant/integrations`. One entry per `CHAT_PROVIDERS` member, in that order. */
export const chatIntegrationsDTOSchema = z.object({
    providers: z.array(chatProviderStateDTOSchema),
    /**
     * Whether the tenant's plan includes `platform.chat_integrations` (Slack, Teams; Pro and above).
     * `false` → Connect, Reconnect and the test message answer 402 `PLAN_UPGRADE_REQUIRED`, and an
     * existing connection stays listed but delivers nothing; the page disables those controls and
     * offers the upgrade instead. Served here, on the list the page already reads, because the
     * billing entitlements read needs `platform.billing.manage`, which a `tenant.admin` lacks.
     *
     * ADDITIVE AND OPTIONAL; ABSENT MEANS INCLUDED — a bundle newer than its API mid-deploy keeps
     * today's controls, and the server's 402 stays the boundary either way.
     */
    planIncludesChat: z.boolean().optional(),
});
/** `POST /api/web/tenant/integrations/:provider/install` — where to send the browser next. */
export const chatIntegrationInstallDTOSchema = z.object({
    authorizeUrl: z.string().url(),
});
/**
 * `POST /api/web/tenant/integrations/:provider/complete` — the two query values the vendor
 * appended to the redirect URI, relayed by the SPA. Bounded: both are opaque vendor/our strings,
 * and an unbounded field is a free memory amplifier.
 */
export const completeChatIntegrationInstallSchema = z
    .object({
    code: z.string().min(1).max(1024),
    state: z.string().min(1).max(2048),
})
    .strict();
/** Cap on the location filter — far above any real tenant, small enough to bound a request. */
export const CHAT_INTEGRATION_MAX_LOCATIONS = 500;
/** `PATCH /api/web/tenant/integrations/:id` — replace the location filter. `[]` = all locations. */
export const updateChatIntegrationSchema = z
    .object({
    locationIds: z.array(z.string().min(1).max(64)).max(CHAT_INTEGRATION_MAX_LOCATIONS),
})
    .strict();
/** `POST /api/web/tenant/integrations/:id/test` — the caller was sent a direct message. */
export const chatIntegrationTestResultDTOSchema = z.object({
    delivered: z.literal(true),
});
/**
 * One host whose directory email matched no member of the connected workspace (PRD-010
 * CHAT-03). Listed on Settings → Integrations so an admin can fix the directory email. No
 * email here: the admin opens the person in the directory to see and correct it.
 */
export const chatUnmatchedHostDTOSchema = z.object({
    personId: z.string().min(1),
    displayName: z.string(),
    /** When the workspace last answered "no such member" for this host. */
    checkedAt: z.string().datetime(),
});
/** Cap on the list — the admin needs the first page to act on, and `total` says how many more. */
export const CHAT_UNMATCHED_HOSTS_MAX = 50;
/** `GET /api/web/tenant/integrations/:id/unmatched-hosts`. Most recently checked first. */
export const chatUnmatchedHostsDTOSchema = z.object({
    hosts: z.array(chatUnmatchedHostDTOSchema),
    total: z.number().int().min(0),
});
/**
 * PRD-010 CHAT-08 — a reply button on a chat message (the host's "On my way" / "Ask them to
 * wait"). The platform names actions with this bounded shape and never interprets them: the app
 * that owns the message's subject (`VISITOR_VISIT` → the visitor app) decides what an action
 * means. SCREAMING_SNAKE_CASE, so a value cannot carry a separator or markup.
 */
export const CHAT_REPLY_TOKEN_PATTERN = /^[A-Z][A-Z0-9_]{0,31}$/;
export const chatReplyTokenSchema = z.string().regex(CHAT_REPLY_TOKEN_PATTERN);
/**
 * What a click came to: recorded (the subject changed), `unchanged` (the same answer again —
 * idempotent), `not_recipient` (the clicker is not the person the message was for),
 * `subject_closed` (e.g. the visit was checked out), `subject_not_found`, or `ignored` (not a
 * reply we understand). Shared by the provider callbacks' logs and the fake simulator's body.
 */
export const CHAT_REPLY_OUTCOMES = [
    'recorded',
    'unchanged',
    'not_recipient',
    'subject_closed',
    'subject_not_found',
    'ignored',
];
export const chatReplyOutcomeSchema = z.enum(CHAT_REPLY_OUTCOMES);
/**
 * `POST /api/web/tenant/integrations/:id/simulate-reply` — ONLY on an integration backed by the
 * `fake` adapter (local dev and e2e; the route answers 404 for every real one, and `fake` is
 * refused in prod). Stands in for a host clicking a button in a workspace the browser cannot
 * reach: `actorEmail` is resolved to a workspace user exactly as the arrival DM resolved the host,
 * and the click then runs the SAME authorization and recording path a signed Slack/Teams click does.
 */
export const simulateChatReplySchema = z
    .object({
    subjectType: chatReplyTokenSchema,
    subjectId: z.string().min(1).max(64),
    action: chatReplyTokenSchema,
    actorEmail: z.string().email().max(254),
})
    .strict();
export const chatReplySimulationDTOSchema = z.object({ outcome: chatReplyOutcomeSchema });
/* ------------------------------------------------------- CHAT-09: shared channels */
/** Cap on channel routes per integration — a few front desks, never hundreds. */
export const CHAT_CHANNEL_ROUTES_MAX = 20;
/** Cap on the channel picker list (the provider is paged up to this). */
export const CHAT_CHANNELS_MAX = 1000;
/**
 * One channel the bot can post to, for the picker. `id` is the vendor's channel id — echoed back
 * on create and RE-CHECKED there against the provider's own list, never trusted as sent.
 */
export const chatChannelDTOSchema = z.object({
    id: z.string().min(1).max(256),
    /** Display name: `#reception` (Slack), `Facilities › Front desk` (Teams team › channel). */
    name: z.string(),
});
/** `GET /api/web/tenant/integrations/:id/channels`. `truncated` when the workspace had more. */
export const chatChannelsDTOSchema = z.object({
    channels: z.array(chatChannelDTOSchema),
    truncated: z.boolean(),
});
/** One shared-channel route. `locationIds` empty = every location. No vendor channel id. */
export const chatChannelRouteDTOSchema = z.object({
    id: z.string().min(1),
    channelName: z.string(),
    locationIds: z.array(z.string()),
    createdAt: z.string().datetime(),
});
/** `GET /api/web/tenant/integrations/:id/channel-routes`, oldest first. */
export const chatChannelRoutesDTOSchema = z.object({
    routes: z.array(chatChannelRouteDTOSchema),
});
/** `POST /api/web/tenant/integrations/:id/channel-routes`. */
export const createChatChannelRouteSchema = z
    .object({
    externalChannelId: z.string().min(1).max(256),
    locationIds: z.array(z.string().min(1).max(64)).max(CHAT_INTEGRATION_MAX_LOCATIONS),
})
    .strict();
/** `PATCH /api/web/tenant/integrations/:id/channel-routes/:routeId` — replace the location filter. */
export const updateChatChannelRouteSchema = z
    .object({
    locationIds: z.array(z.string().min(1).max(64)).max(CHAT_INTEGRATION_MAX_LOCATIONS),
})
    .strict();
//# sourceMappingURL=integrations.js.map